import _extends from "@babel/runtime/helpers/extends";
/*
 *                     NOTICE
 *            COPYRIGHT 2024 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import * as AbReactResources from "abreact/generated/locale/AbReactResources";
import { DEFAULT_DEBOUNCE_DELAY } from "abreact/incubator/ui/component/AbDropdownMachine";
import { ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import { AbClearableTextInput } from "abreact/input/AbClearableTextInput";
import { AbDebouncer } from "abreact/input/AbDebouncer";
import { useCurrentCallback, useStateWithRef } from "abreact/utils/AbHooks";
import * as React from "react";
const extractTextInputProps = props => {
  const {
    abRef,
    disabled,
    placeholder,
    tabIndex,
    __htmlProps,
    autoFocus
  } = props;
  return {
    abRef,
    disabled,
    placeholder: placeholder ?? AbReactResources.filterTextInput_emptyText(),
    tabIndex,
    __htmlProps: {
      ...__htmlProps
    },
    autoFocus
  };
};

/**
 * Warning: If the text input is debounced, use refs and current callbacks
 * inside the onChange rather than state values and props. Otherwise, when onChange
 * is called it may reference stale values.
 *
 * A filter string input component.
 * - Has a clear button
 * - Has a yellow background when filtered
 * - Can debounce end user typing, e.g. when filtering is expensive
 *
 * If end user typing is debounced, call onChange:
 * - After the debounce period is over
 * - Immediately on blur text input
 * - Immediately on enter key
 * - Immediately on clear button
 */
export function AbFilterTextInput(props) {
  const {
    value,
    onChange: onFilter,
    textInputProps,
    textInputDebounce
  } = props;
  const [liveValue, setLiveValue, liveValueRef] = useStateWithRef(value);
  const onFilterCb = useCurrentCallback(onFilter);
  React.useEffect(() => {
    setLiveValue(props.value);
  }, [props.value]);
  const isFiltered = textInputDebounce === "default" ? liveValue.length > 0 : value.length > 0;
  const commonProps = ReactUtils.extractCommonProps(props);
  const className = ReactUtils.componentClassNamesString("ab-filter-text-input-wrapper", commonProps, [isFiltered && "ab-filter-is-active"]);
  const textInputClassName = ReactUtils.classNamesString(["ab-filter-text-input", textInputProps?.className]);
  const buttonProps = {
    className: "ab-filter-text-input-close-button",
    onClick: () => {
      setLiveValue("");
      onFilter("");
    }
  };
  const clearableTextInputProps = {
    ...commonProps,
    className,
    textInputCommonProps: {
      ...ReactUtils.extractCommonProps(textInputProps ?? {}),
      className: textInputClassName
    },
    ...extractTextInputProps(textInputProps ?? {}),
    buttonProps,
    onFocus: textInputProps?.onFocus
  };
  if (textInputDebounce === "default") {
    return /*#__PURE__*/React.createElement(AbDebouncer, {
      onChange: setLiveValue,
      onActivityWindowEnd: () => {
        // uses refs because this is called asynchronously
        onFilterCb(liveValueRef.current);
      },
      activityWindowMillis: DEFAULT_DEBOUNCE_DELAY,
      render: debouncerProps => {
        clearableTextInputProps.__htmlProps ??= {};
        clearableTextInputProps.__htmlProps.onKeyDown = e => {
          debouncerProps.onKeyDown(e);
          textInputProps?.__htmlProps?.onKeyDown?.(e);
        };
        return /*#__PURE__*/React.createElement(AbClearableTextInput, _extends({}, clearableTextInputProps, {
          value: liveValue,
          onChange: debouncerProps.onChange,
          onBlur: e => {
            debouncerProps.onBlur();
            textInputProps?.onBlur?.(e);
          }
        }));
      }
    });
  }
  return /*#__PURE__*/React.createElement(AbClearableTextInput, _extends({}, clearableTextInputProps, {
    value: value
    // does not use a ref because it's called synchronously
    ,
    onChange: onFilter,
    onBlur: e => {
      onFilter(liveValue);
      textInputProps?.onBlur?.(e);
    },
    onEnter: () => onFilter(liveValue)
  }));
}
//# sourceMappingURL=AbFilterTextInput.js.map
